import FastRewindIcon from '@mui/icons-material/FastRewind';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useWavesurfer } from '@wavesurfer/react';
import { useEffect, useMemo, useRef } from 'react';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import Timeline from 'wavesurfer.js/dist/plugins/timeline.js';
import './Scrubber.css';

interface ScrubberProps {
  trackUrl: string;
  onClipSelect?: (start: number, end: number) => void;
  selectedClip?: { start: number; end: number } | null;
  title?: string;
  artist?: string;
  release?: string;
  artworkUrl?: string;
}

interface Region {
  start: number;
  end: number;
}

const Scrubber: React.FC<ScrubberProps> = ({
  trackUrl,
  onClipSelect,
  selectedClip,
  title,
  artist,
  release,
  artworkUrl,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const regionsPlugin = useMemo(() => RegionsPlugin.create(), []);
  const timelinePlugin = useMemo(() => Timeline.create({ height: 16 }), []);
  const currentRegionRef = useRef<{ start: number; end: number } | null>(null);

  const wavesurferConfig = useMemo(
    () => ({
      container: containerRef,
      height: 60,
      waveColor: '#008d9b',
      progressColor: '#7cd8c6',
      cursorColor: '#f69c21',
      cursorWidth: 2,
      barWidth: 0,
      barGap: 0,
      barRadius: 0,
      plugins: [regionsPlugin, timelinePlugin],
      url: trackUrl,
    }),
    [containerRef, regionsPlugin, timelinePlugin, trackUrl],
  );

  const wavesurfer = useWavesurfer(wavesurferConfig);

  // Effect for handling region updates and out-of-bounds
  useEffect(() => {
    if (!wavesurfer.isReady || !wavesurfer.wavesurfer) return;

    const handleRegionUpdated = (region: Region) => {
      const wasPlaying = wavesurfer.isPlaying;
      const now = wavesurfer.wavesurfer?.getCurrentTime();

      if (now && (now < region.start || now > region.end)) {
        wavesurfer.wavesurfer?.setTime(region.start);
      }

      // If it was playing before the region update, resume playback
      if (wasPlaying) {
        // Small delay to ensure the region update is complete
        setTimeout(() => {
          wavesurfer.wavesurfer?.play();
        }, 50);
      }

      onClipSelect?.(region.start, region.end);
    };

    const handleRegionOut = (region: Region) => {
      const wasPlaying = wavesurfer.isPlaying;
      wavesurfer.wavesurfer?.setTime(region.start);

      // If it was playing before going out of bounds, resume playback
      if (wasPlaying) {
        setTimeout(() => {
          wavesurfer.wavesurfer?.play();
        }, 50);
      }
    };

    regionsPlugin.on('region-updated', handleRegionUpdated);
    regionsPlugin.on('region-out', handleRegionOut);

    return () => {
      regionsPlugin.unAll();
    };
  }, [
    onClipSelect,
    regionsPlugin,
    wavesurfer.isPlaying,
    wavesurfer.isReady,
    wavesurfer.wavesurfer,
  ]);

  // Effect for syncing selectedClip prop to region plugin
  useEffect(() => {
    if (!wavesurfer.isReady || !wavesurfer.wavesurfer) return;

    // Get the current region from the plugin
    const regions = regionsPlugin.getRegions
      ? Object.values(regionsPlugin.getRegions())
      : [];
    const region = regions[0];

    // Only update if selectedClip is different from the current region
    if (
      selectedClip &&
      (!region ||
        region.start !== selectedClip.start ||
        region.end !== selectedClip.end)
    ) {
      regionsPlugin.clearRegions();
      regionsPlugin.addRegion({
        start: selectedClip.start,
        end: selectedClip.end,
        minLength: 1,
        maxLength: 60,
        drag: true,
        resize: true,
      });
      currentRegionRef.current = { ...selectedClip };
    }
  }, [regionsPlugin, selectedClip, wavesurfer.isReady, wavesurfer.wavesurfer]);

  const handlePlayPause = () => {
    if (wavesurfer.wavesurfer) {
      if (!wavesurfer.isPlaying) {
        // Get the current region
        const regions = regionsPlugin.getRegions
          ? Object.values(regionsPlugin.getRegions())
          : [];
        const currentRegion = regions[0];

        // If there's a region, start from its beginning
        if (currentRegion) {
          wavesurfer.wavesurfer.setTime(currentRegion.start);
        }
      }
      wavesurfer.wavesurfer.playPause();
    }
  };

  const handleRewind = () => {
    if (wavesurfer.wavesurfer) {
      // Get the current region
      const regions = regionsPlugin.getRegions
        ? Object.values(regionsPlugin.getRegions())
        : [];
      const currentRegion = regions[0];

      // If there's a region, rewind to its start, otherwise rewind to beginning of track
      const rewindTime = currentRegion ? currentRegion.start : 0;
      wavesurfer.wavesurfer.setTime(rewindTime);
    }
  };

  return (
    <Paper elevation={3} className="scrubber-paper">
      <Box className="scrubber-container">
        {(title || artist || release || artworkUrl) && (
          <Box className="scrubber-info">
            {artworkUrl && (
              <img
                src={artworkUrl}
                alt="Track artwork"
                className="scrubber-artwork"
              />
            )}
            <Box className="scrubber-track-info">
              {title && (
                <Typography variant="subtitle1" className="scrubber-title">
                  {title}
                </Typography>
              )}
              {artist && (
                <Typography variant="body2" className="scrubber-artist">
                  {artist}
                </Typography>
              )}
              {release && (
                <Typography variant="body2" className="scrubber-release">
                  {release}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {selectedClip && (
          <Box className="clip-info-container">
            <Typography variant="body2">
              <b>Start:</b>{' '}
              {new Date(Math.round(selectedClip.start) * 1000)
                .toISOString()
                .substr(11, 8)}
            </Typography>
            <Typography variant="body2" className="clip-length">
              Clip length:{' '}
              {new Date(
                Math.round(selectedClip.end - selectedClip.start) * 1000,
              )
                .toISOString()
                .substr(14, 5) + '.000'}
            </Typography>
            <Typography variant="body2">
              <b>End:</b>{' '}
              {new Date(Math.round(selectedClip.end) * 1000)
                .toISOString()
                .substr(11, 8)}
            </Typography>
          </Box>
        )}
        <div ref={containerRef} className="wavesurfer-container" />
        <Box className="controls-container">
          <IconButton
            onClick={handleRewind}
            size="large"
            className="scrubber-rewind-button"
          >
            <FastRewindIcon />
          </IconButton>
          <IconButton
            onClick={handlePlayPause}
            className="scrubber-player-button"
            size="large"
          >
            {wavesurfer.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Box>
        <Box className="time-display-container">
          <Typography variant="body2" className="time-text">
            {new Date(Math.floor(wavesurfer.currentTime) * 1000)
              .toISOString()
              .substr(11, 8)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default Scrubber;
